import React, { useState, useCallback, useMemo } from 'react';
import Modal from 'react-modal';
import ConsularActForm from './ConsularActForm';
import AdminButton from '../../../components/admin/AdminButton';
import { getSupabase } from '../../../supabaseClient';
import toast from 'react-hot-toast';
import { useEffectOnce } from '../../../hooks/useEffectOnce';
import DOMPurify from 'dompurify';
import { useAuth } from '../../../contexts/AuthContext';

// Bind modal to your appElement for accessibility
Modal.setAppElement('#root');

const ConsularActsList = () => {
  // États du composant
  const [consularActs, setConsularActs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedAct, setSelectedAct] = useState(null);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [actToDelete, setActToDelete] = useState(null);
  const [confirmToggleModalOpen, setConfirmToggleModalOpen] = useState(false);
  const [actToToggle, setActToToggle] = useState(null);
  
  // Récupérer l'utilisateur connecté
  const { user } = useAuth();
  
  // Vérifier si l'utilisateur est restreint
  const isRestrictedUser = user?.email === 'contact@cgci-paris.com';

  // Récupérer les actes consulaires avec leurs détails
  const fetchConsularActsWithDetails = useCallback(async () => {
    try {
      const supabase = getSupabase();
      
      // Récupérer les actes consulaires avec leurs catégories
      const { data: actsData, error: actsError } = await supabase
        .from('consular_acts')
        .select(`
          *,
          categories (
            id,
            name
          )
        `);
      
      // Récupérer les catégories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('id, name');
      
      if (actsError || categoriesError) {
        throw actsError || categoriesError;
      }
      
      // Transformer les données pour avoir plus d'informations
      const enrichedActs = actsData.map(act => ({
        ...act,
        categoryName: act.categories?.name || 'Non assigné'
      }));
      
      setConsularActs(enrichedActs);
      setCategories(categoriesData || []);
      
      // Afficher un toast si aucun acte n'est trouvé
      if (!enrichedActs || enrichedActs.length === 0) {
        toast.info('Aucun acte consulaire trouvé');
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des actes consulaires:', err);
      toast.error(`Erreur : ${err.message}`);
      setConsularActs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les actes consulaires une seule fois au montage
  useEffectOnce(() => {
    fetchConsularActsWithDetails();
  });

  // Gérer la recherche d'actes consulaires
  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Filtrer les actes consulaires
  const filteredActs = useMemo(() => {
    return consularActs
      .filter(act => 
        // Filtrer par titre
        act.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        // Filtrer par catégorie si sélectionnée
        (selectedCategory === '' || act.category_id === selectedCategory)
      )
      .reverse(); // Inverser l'ordre des actes
  }, [consularActs, searchTerm, selectedCategory]);

  // Gérer l'édition d'un acte consulaire
  const handleEdit = useCallback((act) => {
    setSelectedAct({
      id: act.id,
      title: act.title,
      description: act.description,
      category_id: act.category_id,
      processing_time: act.processing_time,
      processing_fee: act.processing_fee,
      shipping_fee: act.shipping_fee,
      shipping_fees: act.shipping_fees, // <-- Correction ici
      form_url: act.form_url,
      required_parts: act.required_parts || [{ case: '', parts: [] }]
    });
    setModalIsOpen(true);
  }, []);

  // Gérer le changement d'état (actif/inactif) d'un acte consulaire
  const handleToggleActive = useCallback(async () => {
    if (!actToToggle) return;
    
    const toastId = toast.loading(`${actToToggle.active ? 'Désactivation' : 'Activation'} de l'acte consulaire en cours...`);
    try {
      const { error } = await getSupabase()
        .from('consular_acts')
        .update({ active: !actToToggle.active })
        .eq('id', actToToggle.id);

      if (error) throw error;
      
      // Mettre à jour la liste des actes consulaires après modification
      await fetchConsularActsWithDetails();
      toast.success(`L'acte consulaire a été ${actToToggle.active ? 'désactivé' : 'activé'} avec succès`, { id: toastId });
    } catch (err) {
      console.error(`Erreur lors du changement d'état de l'acte consulaire:`, err);
      toast.error(`Erreur : ${err.message}`, { id: toastId });
    } finally {
      setConfirmToggleModalOpen(false);
      setActToToggle(null);
    }
  }, [actToToggle, fetchConsularActsWithDetails]);

  // Ouvrir la modale de confirmation pour le toggle
  const openToggleConfirmModal = useCallback((act) => {
    setActToToggle(act);
    setConfirmToggleModalOpen(true);
  }, []);

  // Fermer la modale de confirmation pour le toggle
  const closeToggleConfirmModal = useCallback(() => {
    setConfirmToggleModalOpen(false);
    setActToToggle(null);
  }, []);

  // Préparer la suppression d'un acte consulaire
  const prepareDeleteAct = useCallback((act) => {
    setActToDelete(act);
    setConfirmDeleteModalOpen(true);
  }, []);

  // Gérer la suppression confirmée d'un acte consulaire
  const handleConfirmDelete = useCallback(async () => {
    if (!actToDelete) return;

    const toastId = toast.loading('Suppression de l\'acte consulaire...');
    try {
      const { error } = await getSupabase()
        .from('consular_acts')
        .delete()
        .eq('id', actToDelete.id);

      if (error) throw error;

      // Mettre à jour la liste des actes
      setConsularActs(prevActs => prevActs.filter(act => act.id !== actToDelete.id));
      
      toast.success('Acte consulaire supprimé', { id: toastId });
      setConfirmDeleteModalOpen(false);
      setActToDelete(null);
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      toast.error(`Erreur : ${err.message}`, { id: toastId });
    }
  }, [actToDelete]);

  // Fonction pour ouvrir la modale
  const openModal = useCallback(() => {
    setModalIsOpen(true);
  }, []);

  // Fonction pour fermer la modale
  const closeModal = useCallback(() => {
    setModalIsOpen(false);
    setSelectedAct(null);
  }, []);

  // Fonction pour tronquer le texte HTML
  const truncateHtml = (html, maxLength = 200) => {
    if (!html) return '';
    
    // Créer un élément temporaire pour extraire le texte
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = DOMPurify.sanitize(html);
    let text = tempDiv.textContent || tempDiv.innerText || '';
    
    // Tronquer le texte
    if (text.length > maxLength) {
      text = text.substr(0, maxLength) + '...';
    }
    
    return text;
  };

  // Rendu du contenu de la liste
  const renderContent = () => {
    if (loading) {
      return <div className="text-center py-10">Chargement...</div>;
    }

    if (filteredActs.length === 0) {
      return <div className="text-center py-10">Aucun acte consulaire trouvé</div>;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredActs.map(act => (
          <div 
            key={act.id} 
            className="bg-white shadow-md rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col"
          >
            <div className="p-6 flex-grow">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{act.title}</h3>
              </div>
              <div 
                className="text-gray-600 mb-4" 
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(truncateHtml(act.description)) 
                }} 
              />
              <div className="flex flex-col space-y-1 text-sm text-gray-500">
                <span>Catégorie : {act.categoryName}</span>
                <span>Droits de chancellerie : <span className="font-semibold bg-green-100 px-2 py-1 rounded-full text-green-600">{act.processing_fee > 0 ? act.processing_fee.toFixed(2) + ' €' : 'Gratuit'}</span></span>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 mt-auto">
              {!isRestrictedUser ? (
                <div className="flex justify-between space-x-2">
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => openToggleConfirmModal(act)}
                      className={`flex items-center px-4 py-2 ${act.active ? 'bg-green-600' : 'bg-gray-500'} text-white rounded-md hover:opacity-90 transition-colors`}
                      title={act.active ? 'Désactiver l\'acte consulaire' : 'Activer l\'acte consulaire'}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={act.active ? "M5 13l4 4L19 7" : "M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"} />
                      </svg>
                      {act.active ? 'Actif' : 'Inactif'}
                    </button>
                    <button 
                      onClick={() => handleEdit(act)}
                      className="flex items-center px-4 py-2 bg-secondary text-white rounded-md hover:bg-opacity-90 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Modifier
                    </button>
                  </div>
                  <button 
                    onClick={() => prepareDeleteAct(act)}
                    className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="w-full text-center text-gray-500 text-sm">
                  {act.active ? 'Acte consulaire actif' : 'Acte consulaire inactif'}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Gestion des actes consulaires</h2>
        
        {!isRestrictedUser && (
          <AdminButton 
            type="primary" 
            onClick={() => {
              setSelectedAct(null);
              openModal();
            }}
            icon="add"
          >
            Nouvel acte consulaire
          </AdminButton>
        )}
      </div>
      
      {/* Filtres et barre de recherche */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Barre de recherche */}
        <div className="relative">
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Rechercher un acte consulaire..."
            className="tracking-input pl-10 w-full form-input"
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        {/* Filtre par catégorie */}
        <select
          className="tracking-input w-full form-input"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">Toutes les catégories</option>
          {categories.map(category => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>
      
      {renderContent()}
      
      {/* Modal pour le formulaire d'acte consulaire */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Formulaire d'acte consulaire"
        style={{
          maxWidth: '70%',
          width: '70%',
          margin: '2rem auto',
          borderRadius: '0.75rem',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}
        className="ReactModal__Content"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        ariaHideApp={false}
      >
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
          <h3 className="text-xl font-bold text-gray-900">
            {selectedAct ? 'Modifier un acte consulaire' : 'Nouvel acte consulaire'}
          </h3>
          <button 
            type="button"
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="p-6 bg-white overflow-y-auto max-h-[70vh]">
          <ConsularActForm 
            isModal={true}
            onSuccess={() => {
              closeModal();
              fetchConsularActsWithDetails();
            }}
            initialData={selectedAct ? {
              id: selectedAct.id,
              title: selectedAct.title,
              description: selectedAct.description,
              category_id: selectedAct.category_id,
              processing_time: selectedAct.processing_time,
              processing_fee: selectedAct.processing_fee,
              shipping_fee: selectedAct.shipping_fee,
              shipping_fees: selectedAct.shipping_fees,
              form_url: selectedAct.form_url,
              required_parts: selectedAct.required_parts
            } : null}
          />
        </div>
      </Modal>

      {/* Modal de confirmation de suppression */}
      <Modal
        isOpen={confirmDeleteModalOpen}
        onRequestClose={() => setConfirmDeleteModalOpen(false)}
        contentLabel="Confirmation de suppression"
        overlayClassName="ReactModal__Overlay"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        ariaHideApp={false}
      >
        <div className="p-6 text-center">
          <svg 
            className="mx-auto mb-4 w-12 h-12 text-red-600" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="mb-5 text-lg font-normal text-gray-500">
            Êtes-vous sûr de vouloir supprimer cet acte consulaire ?
          </h3>
          <div className="flex justify-center space-x-4">
            <AdminButton 
              type="delete" 
              onClick={handleConfirmDelete}
            >
              Oui, supprimer
            </AdminButton>
            <AdminButton 
              type="secondary" 
              onClick={() => setConfirmDeleteModalOpen(false)}
            >
              Annuler
            </AdminButton>
          </div>
        </div>
      </Modal>

      {/* Modale de confirmation pour activer/désactiver un acte consulaire */}
      <Modal
        isOpen={confirmToggleModalOpen}
        onRequestClose={closeToggleConfirmModal}
        contentLabel="Confirmation de changement d'état"
        overlayClassName="ReactModal__Overlay"
        className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden flex flex-col"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        ariaHideApp={false}
      >
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-bold text-gray-900">
            Confirmation
          </h3>
          <button 
            type="button"
            onClick={closeToggleConfirmModal}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="p-6">
          <p className="text-gray-700 mb-6">
            {actToToggle?.active 
              ? `Êtes-vous sûr de vouloir désactiver l'acte consulaire "${actToToggle?.title}" ?` 
              : `Êtes-vous sûr de vouloir activer l'acte consulaire "${actToToggle?.title}" ?`}
          </p>
          <div className="flex justify-end space-x-4">
            <button
              onClick={closeToggleConfirmModal}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleToggleActive}
              className={`px-4 py-2 ${actToToggle?.active ? 'bg-orange-500' : 'bg-green-600'} text-white rounded-md hover:opacity-90 transition-colors`}
            >
              {actToToggle?.active ? 'Désactiver' : 'Activer'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ConsularActsList;
